import { FC, useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@src/app/contexts/Application';
import { AuthContext } from '@src/app/contexts/Auth';
import { getAdviceCallEvent } from '@src/app/requests/getApplicationData';

import { HttpError } from '../HttpError';

import { getCalendlyUrl } from '.';

interface BookingCalendlyProps {
    onClose: Function;
    successNavLink: string;
}

const BookingCalendly: FC<BookingCalendlyProps> = (props) => {
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    const { setCallDateTime, incomings, applicationId } =
        useContext(ApplicationContext);

    const { getAccessToken, getIdToken } = useContext(AuthContext);

    const notEmployed = incomings.some(
        (incoming: any) => incoming.employmentStatus !== 'Employed',
    );

    const handleClose = () => {
        props.onClose();
        document.body.style.overflow = 'initial';
    };

    useEffect(() => {
        window.addEventListener('keydown', handleClose);
        return () => window.removeEventListener('keydown', handleClose);
    });

    useEffect(() => {
        const handleEvent = async (e: MessageEvent) => {
            if (e.data && e.data.event === 'calendly.event_scheduled') {
                const eventData = e.data.payload;
                const eventUri = eventData?.event?.uri;

                if (!eventUri || !applicationId) {
                    setError('Unable to retrieve booking details.');
                    return;
                }

                const accessToken = await getAccessToken();
                const idToken = await getIdToken();

                if (
                    typeof accessToken !== 'string' ||
                    typeof idToken !== 'string' ||
                    !accessToken ||
                    !idToken
                ) {
                    setError('Unable to authenticate the booking request.');
                    return;
                }

                const response = await getAdviceCallEvent(
                    applicationId,
                    eventUri,
                    accessToken,
                    idToken,
                );

                if (response.success && response.startTime) {
                    setCallDateTime(response.startTime);
                    navigate(props.successNavLink);
                } else {
                    setError(response.error);
                }
            }
        };

        window.addEventListener('message', handleEvent);

        const calendlyUrl = getCalendlyUrl(notEmployed, true);

        const calendlyEmbedMobile = document.getElementById(
            'calendly-embed-mobile',
        );
        const calendlyEmbed = document.getElementById('calendly-embed');

        const isMobileView = window.innerWidth < 1024;

        const targetElement = isMobileView
            ? calendlyEmbedMobile
            : calendlyEmbed;

        if (targetElement?.children.length === 0) {
            window.Calendly.initInlineWidget({
                url: calendlyUrl,
                parentElement: targetElement,
            });
        }

        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('message', handleEvent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {/* Hidden on larger screens */}
            <div className='block lg:hidden p-4 flex justify-center'>
                <div
                    id='calendly-embed-mobile'
                    className='calendly-inline-widget'
                ></div>
            </div>

            {/* Hidden on smaller screens */}
            <div
                className='hidden lg:block backdrop-blur-xs backdrop-filter bottom-0 fixed h-screen left-0 right-0 top-0 w-screen bg-opacity-40 bg-black'
                onClick={handleClose}
            >
                <div className='fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>
                    <div
                        id='calendly-embed'
                        className='calendly-inline-widget'
                    ></div>
                    <script
                        type='text/javascript'
                        src='https://assets.calendly.com/assets/external/widget.js'
                    ></script>
                </div>
            </div>
            {error && <HttpError error={error} />}
        </>
    );
};

export default BookingCalendly;
